import { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { useChartContext } from './ChartProvider'

function CustomChart ({
  seriesId,
  name,
  colorBy,
  legendHoverLink,
  coordinateSystem,
  xAxisIndex,
  yAxisIndex,
  polarIndex,
  geoIndex,
  calendarIndex,
  renderItem,
  itemStyle,
  labelLine,
  labelLayout,
  selectedMode,
  dimensions,
  encode,
  seriesLayoutBy,
  datasetIndex,
  dataGroupId,
  data,
  clip,
  zlevel,
  z,
  silent,
  animation,
  animationThreshold,
  animationDuration,
  animationEasing,
  animationDelay,
  animationDurationUpdate,
  animationEasingUpdate,
  animationDelayUpdate,
  universalTransition,
  tooltip
}) {
  const { setSeries } = useChartContext()
  useLayoutEffect(() => {
    setSeries(seriesId, {
      type: 'custom',
      id: seriesId,
      name,
      colorBy,
      legendHoverLink,
      coordinateSystem,
      xAxisIndex,
      yAxisIndex,
      polarIndex,
      geoIndex,
      calendarIndex,
      renderItem,
      itemStyle,
      labelLine,
      labelLayout,
      selectedMode,
      dimensions,
      encode,
      seriesLayoutBy,
      datasetIndex,
      dataGroupId,
      data,
      clip,
      zlevel,
      z,
      silent,
      animation,
      animationThreshold,
      animationDuration,
      animationEasing,
      animationDelay,
      animationDurationUpdate,
      animationEasingUpdate,
      animationDelayUpdate,
      universalTransition,
      tooltip
    })
  }, [
    setSeries,
    seriesId,
    name,
    colorBy,
    legendHoverLink,
    coordinateSystem,
    xAxisIndex,
    yAxisIndex,
    polarIndex,
    geoIndex,
    calendarIndex,
    renderItem,
    itemStyle,
    labelLine,
    labelLayout,
    selectedMode,
    dimensions,
    encode,
    seriesLayoutBy,
    datasetIndex,
    dataGroupId,
    data,
    clip,
    zlevel,
    z,
    silent,
    animation,
    animationThreshold,
    animationDuration,
    animationEasing,
    animationDelay,
    animationDurationUpdate,
    animationEasingUpdate,
    animationDelayUpdate,
    universalTransition,
    tooltip
  ])

  return null
}

CustomChart.propTypes = {
  seriesId: PropTypes.string,
  /** Series name used for displaying in tooltip and filtering with legend, or updating data and configuration with setOption. **/
  name: PropTypes.string,
  /** The policy to take color from option.color. Valid values:

    'series': assigns the colors in the palette by series, so that all data in the same series are in the same color;
    'data': assigns colors in the palette according to data items, with each data item using a different color. **/
  colorBy: PropTypes.oneOf(['series', 'data']),
  /** Whether to enable highlighting chart when legend is being hovered. **/
  legendHoverLink: PropTypes.bool,
  /** The coordinate used in the series, whose options are:

null or 'none'

No coordinate.

'cartesian2d'

Use a two-dimensional rectangular coordinate (also known as Cartesian coordinate), with xAxisIndex and yAxisIndex to assign the corresponding axis component.

'polar'

Use polar coordinates, with polarIndex to assign the corresponding polar coordinate component.

'geo'

Use geographic coordinate, with geoIndex to assign the corresponding geographic coordinate components.

'calendar'

Use calendar coordinates, with calendarIndex to assign the corresponding calendar coordinate components.

'none'

Do not use coordinate system. **/
  coordinateSystem: PropTypes.string,
  /** Index of x axis to combine with, which is useful for multiple x axes in one chart. **/
  xAxisIndex: PropTypes.number,
  /** Index of y axis to combine with, which is useful for multiple y axes in one chart. **/
  yAxisIndex: PropTypes.number,
  /** Index of polar coordinate to combine with, which is useful for multiple polar axes in one chart. **/
  polarIndex: PropTypes.number,
  /** Index of geographic coordinate to combine with, which is useful for multiple geographic axes in one chart. **/
  geoIndex: PropTypes.number,
  /** Index of calendar coordinates to combine with, which is useful for multiple calendar coordinates in one chart. **/
  calendarIndex: PropTypes.number,
  /** custom series requires developers to write a render logic by themselves. This render logic is called renderItem. **/
  renderItem: PropTypes.func,
  /** Graphic style of , emphasis is the style when it is highlighted, like being hovered by mouse, or highlighted via legend connect. **/
  itemStyle: PropTypes.object,
  /** Configuration of label guide line. **/
  labelLine: PropTypes.object,
  /** Unified layout configuration of labels.

   It provide a chance to adjust the labels' (x, y) position, alignment based on the original layout each series provides.

   This option can be a callback with following parameters. **/
  labelLayout: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /** Selected mode. It is disabled by default, and you may set it to be true to enable it.

   Besides, it can be set to 'single', 'multiple' or 'series', for single selection, multiple selections and whole series selection. **/
  selectedMode: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** dimensions can be used to define dimension info for series.data or dataset.source.

   Notice: if dataset is used, we can definite dimensions in dataset.dimensions, or provide dimension names in the first column/row of dataset.source, and not need to specify dimensions here. But if dimensions is specified here, it will be used despite the dimension definitions in dataset. **/
  dimensions: PropTypes.array,
  /** Define what is encoded to for each dimension of data. **/
  encode: PropTypes.object,
  /** When dataset is used, seriesLayoutBy specifies whether the column or the row of dataset is mapped to the series, namely, the series is "layout" on columns or rows. Optional values:

   'column': by default, the columns of dataset are mapped the series. In this case, each column represents a dimension.
   'row'：the rows of dataset are mapped to the series. In this case, each row represents a dimension. **/
  seriesLayoutBy: PropTypes.string,
  /** If series.data is not specified, and dataset exists, the series will use dataset. datasetIndex specifies which dataset will be used. **/
  datasetIndex: PropTypes.number,
  /** A group ID assigned to all data items in the series.

   This option has a lower priority than groupId, which means when groupId is specified for a certain data item the dataGroupId will be simply ignored for that data item. For more information, please see series.data.groupId. **/
  dataGroupId: PropTypes.string,
  /** Data array of series, which can be in the following forms:

   Notice, if no data specified in series, and there is dataset in option, series will use the first dataset as its datasource. If data has been specified, dataset will not used.

   series.datasetIndex can be used to specify other dataset.

   **/
  data: PropTypes.array,
  /** If clip the overflow on the coordinate system. Clip results varies between series:

   Scatter/EffectScatter：Ignore the symbols exceeds the coordinate system. Not clip the elements.
   Bar：Clip all the overflowed. With bar width kept.
   Line：Clip the overflowed line.
   Lines: Clip all the overflowed.
   Candlestick: Ignore the elements exceeds the coordinate system.
   PictorialBar: Clip all the overflowed. (Supported since v5.5.0)
   Custom: Clip all the olverflowed.
   All these series have default value true except pictorialBar and custom series. Set it to false if you don't want to clip. **/
  clip: PropTypes.bool,
  /** zlevel value of all graphical elements in custom series.

   zlevel is used to make layers with Canvas. Graphical elements with different zlevel values will be placed in different Canvases, which is a common optimization technique. We can put those frequently changed elements (like those with animations) to a separate zlevel. Notice that too many Canvases will increase memory cost, and should be used carefully on mobile phones to avoid crash.

   Canvases with bigger zlevel will be placed on Canvases with smaller zlevel. **/
  zlevel: PropTypes.number,
  /** z value of all graphical elements in custom series, which controls order of drawing graphical components. Components with smaller z values may be overwritten by those with larger z values.

   z has a lower priority to zlevel, and will not create new Canvas. **/
  z: PropTypes.number,
  /** Whether to ignore mouse events. Default value is false, for triggering and responding to mouse events. **/
  silent: PropTypes.bool,
  /** Whether to enable animation. **/
  animation: PropTypes.bool,
  /** Whether to set graphic number threshold to animation. Animation will be disabled when graphic number is larger than threshold. **/
  animationThreshold: PropTypes.number,
  /** Duration of the first animation, which supports callback function for different data to have different animation effect: **/
  animationDuration: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  /** Easing method used for the first animation. Varied easing effects can be found at easing effect example. **/
  animationEasing: PropTypes.string,
  /** Delay before updating the first animation, which supports callback function for different data to have different animation effect. **/
  animationDelay: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  /** Time for animation to complete, which supports callback function for different data to have different animation effect: **/
  animationDurationUpdate: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  /** Easing method used for animation. **/
  animationEasingUpdate: PropTypes.string,
  /** Delay before updating animation, which supports callback function for different data to have different animation effects. **/
  animationDelayUpdate: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  /** Configuration related to universal transition animation.

   Universal Transition provides the ability to morph between any series. With this feature enabled, each time setOption, transitions between series with the same id will be automatically associated with each other.

   One-to-many or many-to-one animations such as drill-down, aggregation, etc. can also be achieved by specifying data items' groupId and childGroupId.

   This can be enabled directly by configuring universalTransition: true in the series. It is also possible to provide an object for more detailed configuration. **/
  universalTransition: PropTypes.object,
  /** tooltip settings in this series. **/
  tooltip: PropTypes.object
}

export default CustomChart
